import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { sanitizeAnalyticsUrl } from '../utils/sanitizeAnalyticsUrl';

/**
 * Supplemental route analytics.
 * The landing `$pageview` is sent by PostHog init (`capture_pageview: true`)
 * and is not gated on auth. This hook sends `$pageview` for later path
 * changes, because that init option does not listen to history changes.
 */
export function usePageViewTracking() {
  const location = useLocation();
  const posthog = usePostHog();
  const previousPathRef = useRef<string>('');

  useEffect(() => {
    const currentPath = location.pathname;

    if (currentPath !== previousPathRef.current && posthog) {
      const isLandingHit = previousPathRef.current === '';
      const pageUrl = sanitizeAnalyticsUrl(
        `${window.location.origin}${location.pathname}${location.search}${location.hash}`,
      );
      const referrer = document.referrer
        ? sanitizeAnalyticsUrl(document.referrer)
        : 'direct';
      posthog.capture('page_viewed', {
        page_path: currentPath,
        page_url: pageUrl,
        page_title: document.title,
        referrer,
        timestamp: new Date().toISOString(),
        source: 'spa_route_hook',
      });

      if (!isLandingHit) {
        posthog.capture('$pageview', {
          title: document.title,
          $current_url: pageUrl,
        });
      }

      previousPathRef.current = currentPath;
    }
  }, [location, posthog]);
} 